import { useEffect } from "react";
import {
    Spinner,
    GuarantorRoute,
    PageFooter,
    PageHeader,
    LoginHeader
} from '@common';
import { Route, Routes, Outlet, useLocation } from 'react-router-dom';

declare global {
    const analytics: {
        page: () => void;
    } | undefined;
}

import {
    AddPayor,
    CheckSession,
    ConfirmPayment,
    DefaultPaymentMethod,
    PaymentMethodSelector,
    Guarantor,
    HomeSelector,
    InformationSafety,
    ManagePayors,
    MyAccount,
    NotFound,
    PaymentHistory,
    PaymentMethodSettings,
    Payor,
    Signup,
    StepEnrollmentPayors,
    UserLogin,
    Welcome,
    ForgotPassword,
    ForgotPasswordInfo,
    ResetPassword,
    ResetPasswordSuccess,
    ResendWelcome,
    ResendWelcomeInfo,
    MakePayment,
    OnboardMultipleAccountsInfo,
    ExistingPaymentMethods,
    SetupAutopay,
    ResolveDefaultPaymentMethods
} from "@components";

const Layout = () => {
    const { hostname } = window.location;
    const firstSubdomain = hostname.split(`.`)[0];
    const isLoginSubdomain = firstSubdomain === `login`;

    return (
        <div className="min-h-screen flex flex-col">
            {isLoginSubdomain ? (
                <LoginHeader id="page-header" />
            ) : (
                <PageHeader id="page-header" />
            )}
            <main className="flex flex-1 pt-6 pb-14 md:pt-8 md:pb-20 w-full max-w-[600px] mx-auto px-4 md:px-8">
                <Spinner />
                <Outlet />
            </main>
            <PageFooter id="page-footer" />
        </div>
    );
};

const App = () => {
    const location = useLocation();

    useEffect(() => {
        if (typeof analytics !== `undefined`) {
            analytics?.page();
        }
    }, [location]);

    return (
        <Routes>
            <Route element={<Layout />}>
                <Route path="/login" element={<UserLogin />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/forgot-password-info" element={<ForgotPasswordInfo />} />
                <Route path="/reset-password/:sessionId" element={<ResetPassword />} />
                <Route path="/reset-password-success" element={<ResetPasswordSuccess />} />
                <Route path="/resend-welcome" element={<ResendWelcome />} />
                <Route path="/resend-welcome-info" element={<ResendWelcomeInfo />} />

                <Route path="/signup/:sessionId" element={<Signup />} />

                <Route element={<CheckSession redirectPath="/login" />}>
                    <Route path="/" element={<HomeSelector />} />
                    <Route path="/myaccount" element={<MyAccount />} />
                    <Route path="/payment-method-settings" element={<PaymentMethodSettings />} />
                    <Route path="/manage-payors" element={<ManagePayors />} />
                    <Route path="/add-payor" element={<AddPayor />} />
                    <Route path="/payment-history" element={<PaymentHistory />} />

                    <Route path="/setup-autopay" element={<SetupAutopay />} />

                    <Route path="/resolve-defaults" element={<ResolveDefaultPaymentMethods />} />

                    <Route path="/make-payment" element={<MakePayment />} />
                    <Route path="/confirm-payment" element={<ConfirmPayment />} />
                    <Route path="/default-payment-method" element={<DefaultPaymentMethod />} />
                    <Route path="/payment-method-selector" element={<PaymentMethodSelector />} />

                    <Route element={<GuarantorRoute />}>
                        <Route path="/guarantor" element={<Guarantor />} />
                    </Route>

                    <Route path="/payor" element={<Payor />} />

                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/information-safety" element={<InformationSafety />} />

                    <Route path="/guarantor/invoice/payors" element={<StepEnrollmentPayors />} />
                    <Route path="/guarantor/multiple-accounts-info" element={<OnboardMultipleAccountsInfo />} />

                    <Route path="/existing-payment-methods" element={<ExistingPaymentMethods />} />
                </Route>
            </Route>
            <Route path="*" element={<NotFound />} />
        </Routes>
    );

};

export default App;
